import { formatDate } from '@/utils/dateHelper.js';
import { CLAIM_TAG_TO_BLOCKED_FIELD } from './data.js';

/**
 * Get subscription status for a reviewer based on claimTag
 * @param {Object} user - User data object
 * @param {string} claimTag - Claim tag for the reviewer
 * @returns {string} - Subscription status (INACTIVE, ACTIVE, WAITING, etc.)
 */
export function getSubscriptionStatus(user, claimTag) {
  if (!claimTag || !user || !user.reviewers) {
    return 'INACTIVE';
  }
  
  const status = user.reviewers[claimTag];
  if (!status) {
    return 'INACTIVE';
  }
  
  // Transform PAID to WAITING
  const upperStatus = status.toUpperCase();
  return upperStatus === 'PAID' ? 'WAITING' : upperStatus;
}

/**
 * Get reviewer-specific data from user object
 * @param {Object} user - User data object
 * @param {string} claimTag - Claim tag for the reviewer
 * @returns {Object} - Reviewer data object
 */
export function getReviewerData(user, claimTag) {
  if (!claimTag || !user) {
    return null;
  }
  
  const dataKey = `${claimTag}_data`;
  return user[dataKey] || null;
}

/**
 * Get formatted expiration date from reviewer data
 * @param {Object} reviewerData - Reviewer data object
 * @returns {string|null} - Formatted expiration date or null
 */
export function getExpirationDate(reviewerData) {
  if (!reviewerData || !reviewerData.expiration_date || !reviewerData.expiration_date.seconds) {
    return null;
  }
  
  return formatDate(reviewerData.expiration_date.seconds, true);
}

/**
 * Get previous subscription status (blocked status)
 * @param {Object} user - User data object
 * @param {string} claimTag - Claim tag for the reviewer
 * @returns {boolean|null} - Previous subscription status or null
 */
export function getPreviousSubscriptionStatus(user, claimTag) {
  if (!claimTag || !user) {
    return null;
  }
  
  const blockedField = CLAIM_TAG_TO_BLOCKED_FIELD[claimTag];
  if (!blockedField) {
    return null;
  }
  
  return user[blockedField] || null;
}

/**
 * Build user data object for a reviewer
 * @param {Object} user - User data object
 * @param {string} claimTag - Claim tag for the reviewer
 * @returns {Object} - User data object for the reviewer
 */
export function buildReviewerUserData(user, claimTag) {
  const reviewerData = getReviewerData(user, claimTag);
  
  return {
    subscriptionStatus: getSubscriptionStatus(user, claimTag),
    refCode: (reviewerData && reviewerData.gcash_refno) ? reviewerData.gcash_refno : null,
    expirationDate: getExpirationDate(reviewerData),
    duration: (reviewerData && reviewerData.duration) ? reviewerData.duration : null,
    durationInWords: (reviewerData && reviewerData.durationInWords) ? reviewerData.durationInWords : null,
    hasPreviousSubscription: getPreviousSubscriptionStatus(user, claimTag),
  };
}

